/**
 * Where a part's card sits on a laptop: under the part, above it, beside it, or over a tall part at the
 * point clicked, with its arrow on the part. The side is chosen once, when the card opens, so it never jumps
 * while the conversation grows; after that the card only follows the part as the page scrolls.
 */
import { useLayoutEffect, useRef } from "react";

/** A part's box on the screen. */
export interface PartBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

type Mode = "below" | "above" | "side" | "tall";

/** What one opening remembers: the side it chose, and the point clicked inside the part. */
interface Opening {
  key: string;
  mode: Mode | null;
  dx: number;
  dy: number;
  /** The part's box against the main column, kept for while the part is drawn as it was. */
  kept: PartBox | null;
}

interface Place {
  /** The card's key; a new key is a new opening. */
  key: string | null;
  /** The part on the screen now, or null when it is not drawn. */
  part: () => PartBox | null;
  /** The part is a pin on the timeline: its card may sit above it, beside the room. */
  pinned?: boolean;
  /** The whole page's card: it may lean over the guide on the left. */
  whole?: boolean;
  /** Where the click that opened it landed, inside the part. */
  at?: { dx: number; dy: number } | null;
  /** Anything that moves the part: the step's own redraws. */
  deps: readonly unknown[];
}

/** The card's tallest, and the room a card keeps from the window's edge. */
const MAXH = 560;

/** The least a card is given: a tall part's card sits under or over the part when this much fits, never on its lines. */
const MINH = 300;

/** The top the card stays under: the step's header, which sticks under the shell's bar. */
function topOff(): number {
  const sh = document.querySelector(".bw-sh");
  return sh ? Math.min(sh.getBoundingClientRect().bottom, 320) + 8 : 150;
}

/** One placement of `card` (inside `host`, the step's `.bw-cards`) against the part. */
function placeOnce(card: HTMLElement, host: HTMLElement, o: Opening, r: PartBox, p: Place, choose: boolean): number {
  const main = host.closest(".bw-main")?.getBoundingClientRect() ?? host.getBoundingClientRect();
  const W = card.offsetWidth;
  const tall = r.height > 150;
  const bottom = r.top + r.height;
  const top0 = topOff() - 10;
  const room = window.innerHeight - 12;
  const px = tall ? r.left + Math.min(Math.max(o.dx, 20), r.width - 20) : r.left + Math.min(r.width / 2, 48);
  const cy = r.top + Math.min(Math.max(o.dy, 10), r.height - 10);
  const roomL = r.left - main.left;
  const roomR = main.right - (r.left + r.width);
  let lift = 0;
  if (!o.mode || choose) {
    const fitsBelow = bottom + 14 + MAXH <= room;
    const need = bottom + 14 + MAXH - room;
    if (tall && Math.max(roomL, roomR) >= 200) o.mode = "side";
    else if (fitsBelow) o.mode = "below";
    else if (!p.pinned && choose && r.top - need >= top0 + 8) {
      o.mode = "below";
      lift = need;
    } else if (r.top - MAXH - 14 >= top0 || p.pinned) o.mode = "above";
    // Why: a card at the click point sits over the part's other lines, and a line can only be typed in when the card is off it.
    else if (tall && bottom + 14 + MINH <= room) o.mode = "below";
    else if (tall && r.top - MINH - 14 >= top0) o.mode = "above";
    else if (tall) {
      o.mode = "tall";
      lift = choose ? Math.max(0, Math.min(cy + 16 + MAXH - room, cy - top0 - 40)) : 0;
    } else o.mode = "side";
  }
  const mode = o.mode;
  // Why: the conversation always fits the window; a long thread scrolls inside the card instead of off the screen.
  const cap = mode === "above" ? r.top - 14 - top0 : room - (mode === "below" ? Math.max(bottom + 14, top0) : mode === "tall" ? cy + 16 : top0);
  card.style.maxHeight = `${Math.max(300, Math.min(680, cap))}px`;
  const H = card.offsetHeight;
  // Why the click point for a tall part: its arrow points at the line that was pressed, not at the part's corner.
  let x = mode === "tall" || (tall && mode !== "side") ? px - 44 : Math.min(r.left, px - 30);
  let y: number;
  if (mode === "tall") y = cy + 16;
  // Why: when the part scrolls up under the header, the conversation stays in view instead of sliding under it.
  else if (mode === "below") y = Math.max(bottom + 14, top0);
  else if (mode === "above") y = r.top - H - 14;
  else {
    // Why: beside the thing, on the side with more room, so the words being changed stay in view.
    x = roomR >= W + 16 ? r.left + r.width + 16 : roomL >= W + 16 ? r.left - W - 16 : roomR > roomL ? main.right - W : main.left;
    y = Math.max(top0, Math.min(tall ? cy - 40 : r.top, room - H));
  }
  // Why: the whole page's card may lean over the guide on the left, so more of the page it changes stays in view.
  const rail = document.querySelector(".bw-rail")?.getBoundingClientRect();
  const minX = p.whole && rail && rail.width > 0 ? rail.left : main.left;
  if (p.whole && mode !== "side" && mode !== "tall") x = r.left - (main.left - minX) + 4;
  // Why: a card for the timeline sits beside the room, so the moment it changes stays in view in the picture.
  const col = p.pinned ? document.querySelector(".bw-roomcol")?.getBoundingClientRect() : null;
  if (col && mode === "above") x = Math.max(x, col.right + 14);
  x = Math.max(minX, Math.min(x, main.right - W));
  const h = host.getBoundingClientRect();
  card.style.left = `${x - h.left}px`;
  card.style.top = `${y - h.top}px`;
  card.style.setProperty("--ax", `${Math.max(22, Math.min(W - 22, px - x))}px`);
  card.classList.toggle("above", mode === "above");
  card.classList.toggle("side", mode === "side");
  return lift;
}

/** The scrolling area the walk sits in: the shell's main column, or the window. */
function scrollerOf(el: HTMLElement): { scrollBy: (o: ScrollToOptions) => void } {
  const m = el.closest<HTMLElement>(".main");
  return m && m.scrollHeight > m.clientHeight ? m : window;
}

/**
 * Keeps the open card of a step beside its part. `host` is the step's `.bw-cards`; its first child is the card.
 * Returns the ref to put on `.bw-cards`.
 */
export function useCardPlace(p: Place) {
  const host = useRef<HTMLDivElement>(null);
  const opening = useRef<Opening | null>(null);
  const latest = useRef(p);
  latest.current = p;

  const place = (choose: boolean) => {
    const q = latest.current;
    const h = host.current;
    const card = h?.firstElementChild as HTMLElement | null;
    if (!h || !card || !q.key) return;
    let o = opening.current;
    if (!o || o.key !== q.key) {
      o = { key: q.key, mode: null, dx: q.at?.dx ?? 0, dy: q.at?.dy ?? 0, kept: null };
      opening.current = o;
      choose = true;
    }
    const main = h.closest(".bw-main")?.getBoundingClientRect() ?? h.getBoundingClientRect();
    const now = q.part();
    let r: PartBox | null = null;
    if (now) {
      r = now;
      o.kept = { left: now.left - main.left, top: now.top - main.top, width: now.width, height: now.height };
      if (!q.at && choose) {
        o.dx = now.width / 2;
        o.dy = now.height / 2;
      }
    } else if (o.kept) r = { ...o.kept, left: main.left + o.kept.left, top: main.top + o.kept.top };
    if (!r) return;
    const lift = placeOnce(card, h, o, r, q, choose);
    if (lift > 0) scrollerOf(h).scrollBy({ top: lift, behavior: "smooth" });
  };

  // A new opening chooses its side; every redraw of the step after that only follows the part.
  useLayoutEffect(() => {
    if (!p.key) {
      opening.current = null;
      return;
    }
    place(false);
    // `place` reads the latest props through a ref; the key and the step's own redraws are what move the card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.key, ...p.deps]);

  useLayoutEffect(() => {
    if (!p.key) return;
    let raf = 0;
    const follow = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        place(false);
      });
    };
    window.addEventListener("scroll", follow, { passive: true, capture: true });
    window.addEventListener("resize", follow);
    // Why: a thread that grows makes the card taller, and above the part its top must move up with it.
    const card = host.current?.firstElementChild;
    const ro = card && typeof ResizeObserver !== "undefined" ? new ResizeObserver(follow) : null;
    if (card) ro?.observe(card);
    return () => {
      window.removeEventListener("scroll", follow, { capture: true });
      window.removeEventListener("resize", follow);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
    // `place` reads the latest props through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.key]);

  return host;
}
